import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import api from "../actions/api"; // Your shared axios instance
import { hideLoader, showLoader } from "./loaderSlice";

export const getSpeciesList = createAsyncThunk(
  "getSpeciesList",
  async (url = "", thunkAPI) => {
    try {
      thunkAPI.dispatch(showLoader());
      const res = await api({ contentType: true, auth: true }).get(
        `/clinic/species${url}`,
      );

      if (res.status === 200) {
        thunkAPI.dispatch(hideLoader());
        return res?.data?.data;
      }
      thunkAPI.dispatch(hideLoader());
      return [];
    } catch (err) {
      thunkAPI.dispatch(hideLoader());
      return thunkAPI.rejectWithValue("Failed to fetch species");
    }
  },
);

export const createSpecies = createAsyncThunk(
  "createSpecies",
  async ({ speciesName }, thunkAPI) => {
    try {
      thunkAPI.dispatch(showLoader());
      const res = await api({ contentType: true, auth: true }).post(
        `/create/species?speciesName=${speciesName}`,
      );

      if (res.status === 200) {
        thunkAPI.dispatch(hideLoader());
        return res?.data?.data;
      }
      thunkAPI.dispatch(hideLoader());
      return thunkAPI.rejectWithValue("Failed to create species");
    } catch (err) {
      thunkAPI.dispatch(hideLoader());
      return thunkAPI.rejectWithValue(err?.response?.data || err.message);
    }
  },
);

export const updateSpecies = createAsyncThunk(
  "updateSpecies",
  async ({ speciesId, speciesName }, thunkAPI) => {
    try {
      thunkAPI.dispatch(showLoader());
      const res = await api({ contentType: true, auth: true }).put(
        `/update/species/${speciesId}?speciesName=${speciesName}`,
      );

      if (res.status === 200) {
        thunkAPI.dispatch(hideLoader());
        return res?.data?.data;
      }
      thunkAPI.dispatch(hideLoader());
      return thunkAPI.rejectWithValue("Failed to update species");
    } catch (err) {
      thunkAPI.dispatch(hideLoader());
      return thunkAPI.rejectWithValue(err?.response?.data || err.message);
    }
  },
);

export const deleteSpecies = createAsyncThunk(
  "deleteSpecies",
  async ({ speciesId }, thunkAPI) => {
    try {
      thunkAPI.dispatch(showLoader());
      const res = await api({ contentType: true, auth: true }).delete(
        `/delete/species/${speciesId}`,
      );

      if (res.status === 200) {
        thunkAPI.dispatch(hideLoader());
        return speciesId; // Return ID to remove from list
      }
      thunkAPI.dispatch(hideLoader());
      return thunkAPI.rejectWithValue("Failed to delete species");
    } catch (err) {
      thunkAPI.dispatch(hideLoader());
      return thunkAPI.rejectWithValue(err?.response?.data || err.message);
    }
  },
);

const speciesSlice = createSlice({
  name: "species",
  initialState: {
    list: [],
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getSpeciesList.fulfilled, (state, action) => {
        state.list = action.payload ?? [];
        state.loading = false;
        state.error = null;
      })
      .addCase(createSpecies.fulfilled, (state, action) => {
        state.list.push(action.payload);
        state.loading = false;
        state.error = null;
      })
      .addCase(updateSpecies.fulfilled, (state, action) => {
        const updated = action.payload;
        const index = state.list.findIndex(
          (s) => s.speciesId === updated.speciesId,
        );
        if (index !== -1) {
          state.list[index] = updated;
        }
        state.loading = false;
        state.error = null;
      })
      .addCase(deleteSpecies.fulfilled, (state, action) => {
        const id = action.payload;
        state.list = state.list.filter((s) => s.speciesId !== id);
        state.loading = false;
        state.error = null;
      })
      .addCase(getSpeciesList.pending, (state) => {
        state.loading = true;
      })
      .addCase(createSpecies.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateSpecies.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteSpecies.pending, (state) => {
        state.loading = true;
      })
      .addCase(getSpeciesList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Error fetching species.";
      });
  },
});

export default speciesSlice.reducer;
